import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { Button, IconButton, Skeleton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useStyles } from './styles';
import { useCan } from '../../../auth/useCan';
import { ORDERS_RW } from '../../../auth/requirements';
import PageContainer from '../../../components/PageContainer';
import { PageHeader, StatusChip } from '../../../components/ui';
import { IStore } from '../../../store/types';
import { Orders } from '../../../store/slices';
import GeneralForm from '../../../components/GeneralForm';
import OrderCancelForm from '../../../components/OrderCancelForm';
import OrderHoldState from '../../../components/OrderHoldState';
import OrderEditForm from '../../../components/OrderEditForm';
import { EXTRA_FEATURES_CLIENT_IDS } from '../../../constants';
import { companyNameFor } from '../../../components/LeftNavigationBar/navConfig';
import { orderStatus } from '../../../lib/orderStatus';
import { formatDate } from '../../../lib/formatDate';

const getID = (id) => `a${id}a`;

const DEFAULT_PAGING_STATE = {
    perPage: 100,
    page: 1,
    total: 0,
    sort: 'createdAt',
    sortDirection: 'DESC',
};

/** Statuses in which Hold and Edit are allowed (Created, Production, Printed, Packaged). */
const OPEN_STATUSES = [1, 2, 10, 11];
/** Statuses in which Cancel and Decline are no longer possible (Shipped, Canceled, Declined). */
const CLOSED_STATUSES = [3, 5, 12];

const OneOrder = () => {
    const { id, companyId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const classes = useStyles();
    const [isCancelPopUp, setCancelPopUp] = useState(false);
    const [isDeclinePopUp, setDeclinePopUp] = useState(false);
    const [isHoldPopUp, setHoldPopUp] = useState(false);
    const [isEditPopUp, setEditPopUp] = useState(false);

    const order = useSelector<IStore, any>((state) => ({
        data: { ...state.orders.data[getID(id) || ''] },
        status: state.orders.status,
        queryParams: state.orders.queryParams,
    }));
    const ordersList = useSelector<IStore, any[]>((state) => [...(state.orders.ordersList || '')]);
    const canManageOrders = useCan(ORDERS_RW);

    const getOrder = (orderId, clientId) => {
        if (ordersList.length < 1) {
            dispatch<any>(Orders.addOrders({ ...DEFAULT_PAGING_STATE, companyId: clientId || '' })).then(() => {
                dispatch<any>(Orders.getOrder({ id: orderId || '', companyId: clientId || '' }));
            });
        } else {
            dispatch<any>(Orders.getOrder({ id: orderId || '', companyId: clientId || '' }));
        }
    };

    const currentIndex = ordersList.indexOf(Number(id));
    const previousOrder = currentIndex > 0 ? ordersList[currentIndex - 1] : null;
    const nextOrder = currentIndex >= 0 && currentIndex < ordersList.length - 1 ? ordersList[currentIndex + 1] : null;

    const checkNextOrders = () => {
        if (currentIndex === ordersList.length - 2 && ordersList.length !== order.queryParams.total) {
            dispatch<any>(Orders.addOrders({ ...order.queryParams, companyId: companyId || '', page: order.queryParams.page + 1 }));
        }
    };

    const goTo = (orderId) => {
        navigate(`/${order.data.clientId}/orders/${orderId}/details`);
        getOrder(orderId, order.data.clientId);
    };
    const goPrevious = () => previousOrder && goTo(previousOrder);
    const goNext = () => {
        if (!nextOrder) return;
        goTo(nextOrder);
        checkNextOrders();
    };

    // Arrow keys move between orders. The listener is registered once and reads the latest handlers
    // and dialog state through a ref, so it never acts on a stale closure.
    const anyDialogOpen = isCancelPopUp || isDeclinePopUp || isHoldPopUp || isEditPopUp;
    const keyboard = useRef({ goNext, goPrevious, anyDialogOpen });
    keyboard.current = { goNext, goPrevious, anyDialogOpen };
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
            if (keyboard.current.anyDialogOpen) return;
            const target = event.target as HTMLElement | null;
            // Typing, open select lists and menus keep their own arrow-key behavior.
            if (target?.closest?.('input, textarea, select, [role="dialog"], [role="listbox"], [role="menu"], [role="combobox"]')) return;
            if (event.key === 'ArrowRight') keyboard.current.goNext();
            if (event.key === 'ArrowLeft') keyboard.current.goPrevious();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        // Mount-only fetch, as before: in-page navigation calls getOrder itself (goTo).
        getOrder(id, companyId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const data = order.data;
    const resolved = order.status === 'resolved' && Object.keys(data).length !== 0;
    const status = orderStatus(data.orderStatusId);
    const company = companyNameFor(companyId);
    const canHoldOrEdit = OPEN_STATUSES.includes(data.orderStatusId);
    const canCancel = !CLOSED_STATUSES.includes(data.orderStatusId);
    const isCanceled = data.orderStatusId === 5;

    const pager = (
        <div className={classes.pager} role="group" aria-label="Previous and next order">
            <Tooltip title="Previous order (←)">
                <span>
                    <IconButton size="small" onClick={goPrevious} disabled={!previousOrder} aria-label="Previous order">
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <span className={classes.pagerText} title="Position among the orders loaded from the list">
                {currentIndex >= 0 ? `${currentIndex + 1} / ${ordersList.length}` : '—'}
            </span>
            <Tooltip title="Next order (→)">
                <span>
                    <IconButton size="small" onClick={goNext} disabled={!nextOrder} aria-label="Next order">
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </div>
    );

    return (
        <PageContainer constrained>
            {resolved && (
                <PageHeader
                    backTo={{ to: `/${companyId}/orders`, label: `${company} orders` }}
                    eyebrow={company}
                    title={`Order #${data.orderNumber}`}
                    badges={(
                        <>
                            <StatusChip tone={status.tone}>{status.label}</StatusChip>
                            {data.onHold ? <StatusChip tone="warning" dot>On hold</StatusChip> : null}
                        </>
                    )}
                    meta={(
                        <>
                            <span>External ID <span className={classes.mono}>{data.externalOrderId || '—'}</span></span>
                            <span>Created {formatDate(data.createdAt, { time: true })}</span>
                            <span>Updated {formatDate(data.updatedAt, { time: true })}</span>
                        </>
                    )}
                    actions={(
                        <>
                            {pager}
                            {canManageOrders && !isCanceled && (
                                <>
                                    <Button
                                        variant="outlined"
                                        startIcon={data.onHold ? <PlayCircleOutlineIcon /> : <PauseCircleOutlineIcon />}
                                        onClick={() => setHoldPopUp(true)}
                                        disabled={!canHoldOrEdit}
                                    >
                                        {data.onHold ? 'Unhold' : 'Hold'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<CancelOutlinedIcon />}
                                        onClick={() => setCancelPopUp(true)}
                                        disabled={!canCancel}
                                    >
                                        Cancel
                                    </Button>
                                    {EXTRA_FEATURES_CLIENT_IDS.includes(String(companyId)) && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<BlockOutlinedIcon />}
                                            onClick={() => setDeclinePopUp(true)}
                                            disabled={!canCancel}
                                        >
                                            Decline
                                        </Button>
                                    )}
                                    <Button
                                        variant="contained"
                                        startIcon={<EditOutlinedIcon />}
                                        onClick={() => setEditPopUp(true)}
                                        disabled={!canHoldOrEdit}
                                    >
                                        Edit
                                    </Button>
                                </>
                            )}
                        </>
                    )}
                />
            )}
            {resolved && isCanceled && canManageOrders && (
                <div className={classes.banner} role="status">
                    <CancelOutlinedIcon aria-hidden="true" />
                    This order was canceled {data.requireCancelPayment === 1 ? 'with a fee' : 'without a fee'}.
                </div>
            )}
            <OrderCancelForm isPopUp={isCancelPopUp} popUpHandler={() => setCancelPopUp(false)} getOrder={() => getOrder(id, companyId)} />
            <OrderCancelForm isPopUp={isDeclinePopUp} popUpHandler={() => setDeclinePopUp(false)} getOrder={() => getOrder(id, companyId)} actionType="decline" />
            <OrderHoldState isPopUp={isHoldPopUp} popUpHandler={() => setHoldPopUp(false)} holdStatus={data.onHold} />
            {resolved && <OrderEditForm isPopUp={isEditPopUp} popUpHandler={() => setEditPopUp(false)} data={data} />}
            {resolved && <GeneralForm data={order} />}
            {order.status === 'loading' && (
                <div className={classes.skeletons} role="status" aria-label="Loading order">
                    <Skeleton animation="wave" height={72} />
                    <Skeleton animation="wave" variant="rounded" height={320} />
                    <Skeleton animation="wave" variant="rounded" height={200} />
                </div>
            )}
        </PageContainer>
    );
};

export default OneOrder;
